/**
 * Create Reference Node Configuration - creates a trace/reference between the
 * source item and a related item discovered by an upstream Item Search /
 * Reference Graph Query step.
 */

import React, { useState } from 'react';
import type { PipelineNode } from '../types/pipeline';
import './CreateReferenceConfig.css';

const REFERENCE_TYPE_OPTIONS = ['REFERENCE', 'ASSOCIATION', 'OUTLINE_PARENT', 'INLINE_REFERENCE'] as const;

export interface CreateReferenceNodeConfig {
  reference_type: string;
  field_id: string;
  direction: 'item_to_related' | 'related_to_item';
  cardinality: 'per_related_item';
}

interface CreateReferenceConfigProps {
  node: PipelineNode;
  onSave: (config: CreateReferenceNodeConfig) => void;
  onClose: () => void;
}

// cardinality is fixed at 'per_related_item' (enforced by validate_workflow_integrity)
// and not user-editable, same as how ItemSearchConfig derives search_kind rather than
// exposing it as a picker.
export const CreateReferenceConfig: React.FC<CreateReferenceConfigProps> = ({ node, onSave, onClose }) => {
  const existing = node.config as Partial<CreateReferenceNodeConfig> | undefined;

  const [referenceType, setReferenceType] = useState<string>(existing?.reference_type ?? 'REFERENCE');
  const [fieldId, setFieldId] = useState<string>(existing?.field_id ?? 'RQ_N/A');
  const [direction, setDirection] = useState<'item_to_related' | 'related_to_item'>(
    existing?.direction ?? 'item_to_related'
  );

  const canSave = referenceType.trim() !== '' && fieldId.trim() !== '';

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      reference_type: referenceType,
      field_id: fieldId,
      direction,
      cardinality: 'per_related_item',
    });
    onClose();
  };

  return (
    <div className="create-reference-config">
      <div className="config-header">
        <div>
          <h3>{node.label || 'Create Reference'}</h3>
          <p className="section-description">
            Creates a trace/reference between the source item and a related item discovered upstream
          </p>
        </div>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="config-body">
        <div className="config-section">
          <div className="condition-row">
            <label>Reference Type</label>
            <select
              className="condition-input"
              value={referenceType}
              onChange={(e) => setReferenceType(e.target.value)}
            >
              {REFERENCE_TYPE_OPTIONS.map((rt) => (
                <option key={rt} value={rt}>{rt}</option>
              ))}
            </select>
          </div>

          <div className="condition-row">
            <label>Field Id</label>
            <input
              type="text"
              className="condition-input"
              value={fieldId}
              onChange={(e) => setFieldId(e.target.value)}
              placeholder="RQ_N/A"
            />
          </div>

          <div className="condition-row">
            <label>Direction</label>
            <select
              className="condition-input"
              value={direction}
              onChange={(e) => setDirection(e.target.value as 'item_to_related' | 'related_to_item')}
            >
              <option value="item_to_related">Source item → Related item</option>
              <option value="related_to_item">Related item → Source item</option>
            </select>
          </div>
        </div>

        <div className="config-help">
          <h4>How this works</h4>
          <ul>
            <li>
              Must depend on exactly one upstream <strong>Item Search</strong> or{' '}
              <strong>Reference Graph Query</strong> node - it creates one reference per discovered
              (item, related item) pair.
            </li>
            <li>
              <strong>Direction</strong> controls which item is the "from" side and which is the "to"
              side of the created reference.
            </li>
          </ul>
        </div>
      </div>

      <div className="config-footer">
        <button className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSave} disabled={!canSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
};
